import { GetStaticPropsContext } from 'next'
import { useRouter } from 'next/router'
import { FahrlehrerCh } from '@/src/@types/blinkadmin'
import { PageParams, PreviewData } from '@/src/@types/infrastructur'
import { BaseDataType } from '@/src/@types/statamic'
import { PageProps } from '@/src/@types/util'
import CookieBanner from '@/src/components/elements/CookieBanner'
import {
  buildBreadcrumbs,
  getLocationBreadcrumb,
  TEACHER_LOCATION_BREADCRUMB,
} from '@/src/components/flch/breadcrumbs'
import BreadcrumbListJsonLd from '@/src/components/flch/components/BreadcrumbListJsonLd'
import PersonJsonLd from '@/src/components/flch/components/PersonJsonLd'
import BaseLayout from '@/src/components/flch/navigation/BaseLayout'
import Header from '@/src/components/flch/profile/Header'
import Images from '@/src/components/flch/profile/Images'
import Info from '@/src/components/flch/profile/Info'
import LocationMap from '@/src/components/flch/profile/LocationMap'
import MoreInstructors from '@/src/components/flch/profile/MoreInstructors'
import MoreLocations from '@/src/components/flch/profile/MoreLocations'
import Nav from '@/src/components/flch/profile/Nav'
import Prices from '@/src/components/flch/profile/Prices'
import QuestionTopics from '@/src/components/flch/profile/QuestionTopics'
import Reviews from '@/src/components/flch/profile/Reviews'
import TrialLessonCta from '@/src/components/flch/profile/TrialLessonCta'
import MetaTags from '@/src/components/MetaTags'
import FlchBaseData from '@/src/graphql/FlchBaseData.graphql'
import { filterNavs } from '@/src/lib/filterNavs'
import { ensureTrailingSlash } from '@/src/lib/str'
import { request, statamicConfig } from '@/src/StatamicGraphQLClient'

const BASE_URL = process.env.BASE_URL

type Props = PageProps<typeof getStaticProps>

export async function getStaticProps(
  context: GetStaticPropsContext<PageParams, PreviewData>,
  values: FahrlehrerCh.TeacherProfile
) {
  const { navs, settings } = await request<BaseDataType>(
    FlchBaseData,
    {
      locale: `${process.env.NEXT_PUBLIC_STATAMIC_SITE_HANDLE}_de`,
      settingsHandle: statamicConfig.settingsHandle,
    },
    context.previewData?.statamicToken
  )

  const filteredNavs = filterNavs(navs)

  const uri = context.params?.uri ? context.params?.uri?.join('/') : ''

  return {
    props: {
      navs: filteredNavs,
      settings,
      fullUrl: uri ? BASE_URL + '/' + ensureTrailingSlash(uri) : BASE_URL,
      values,
    },
  }
}

const Profile = ({ navs, settings, fullUrl, values }: Props) => {
  const router = useRouter()

  const queryLocationId = parseInt(router.query.location as string, 10)

  let selectedLocation: FahrlehrerCh.TeacherProfileLocation | undefined
  if (queryLocationId) {
    selectedLocation = values.locations.find(
      (location) => location.id === queryLocationId
    )
  }
  if (!selectedLocation && values.preferredLocationId) {
    selectedLocation = values.locations.find(
      (location) => location.id === values.preferredLocationId
    )
  }
  if (!selectedLocation) {
    selectedLocation = values.locations[0]
  }

  const breadcrumbs = buildBreadcrumbs(
    values.name,
    TEACHER_LOCATION_BREADCRUMB,
    getLocationBreadcrumb(selectedLocation)
  )

  return (
    <>
      <MetaTags
        seoTitle={`${values.name}: Fahrlehrer:in in ${selectedLocation.name} | Termin online buchen`}
        seoSummary={`${values.name}, deine Fahrlehrperson in ${selectedLocation.name} bei BLINK ✓ Profil und Bewertungen ansehen ✓ Termine online buchen ✓ ►Jetzt Probelektion bei ${values.name} buchen.`}
        title={`${values.name}: Fahrlehrer:in in ${selectedLocation.name} | Termin online buchen`}
        description={`${values.name}, deine Fahrlehrperson in ${selectedLocation.name} bei BLINK ✓ Profil und Bewertungen ansehen ✓ Termine online buchen ✓ ►Jetzt Probelektion bei ${values.name} buchen.`}
        fullUrl={fullUrl}
      />
      <CookieBanner />
      <BreadcrumbListJsonLd breadcrumbs={breadcrumbs} fullUrl={fullUrl} />
      <PersonJsonLd fullUrl={fullUrl} {...values} />
      <BaseLayout navs={navs} settings={settings}>
        <Header
          selectedLocation={selectedLocation}
          breadcrumbs={breadcrumbs}
          {...values}
        />
        <Nav
          hasQuestions={
            values.questionAnswers.length > 0 ||
            values.conversationTopics.length > 0
          }
          hasReviews={values.reviews.length > 0}
        />
        <Info selectedLocation={selectedLocation} {...values} />
        <LocationMap selectedLocation={selectedLocation} {...values} />
        <QuestionTopics {...values} />
        <Images {...values} />
        <Reviews {...values} />
        <Prices selectedLocation={selectedLocation} {...values} />
        <TrialLessonCta selectedLocation={selectedLocation} {...values} />
        <MoreInstructors selectedLocation={selectedLocation} {...values} />
        <MoreLocations selectedLocation={selectedLocation} {...values} />
      </BaseLayout>
    </>
  )
}

export default Profile
